import React, { useEffect, useMemo, useState } from 'react'

import moment from 'moment'

import { AvailabilityIF } from '@driftt/app-config/dist/utils/driftTypes'
import { Paginator, Table } from '@driftt/tide-table'

import { makeUnique } from 'api/report/realTime/helpers'
import ChartContainer from 'components/core/ChartContainer'
import { ChartNoDataState } from 'components/core/ChartStates'
import {
  renderUserCell,
  renderUserCellWithAvailability,
} from 'components/core/UserCell'
import SweetBolt from 'layouts/RealTimeDashboard/components/CustomIcons/SweetBolt'
import { ModalContentProps } from 'layouts/RealTimeDashboard/components/DrilldownModal/useDrilldownConfig'
import { EmptyTablePlaceholder } from 'layouts/RealTimeDashboard/components/TableTooltipHelpers'
import { QualifiedBy } from 'layouts/RealTimeDashboard/constants'
import {
  renderCQLScoreCell,
  renderGoToChatButton,
  renderQualifiedByCell,
} from 'layouts/RealTimeDashboard/helpers'
import { useLeadsQualifiedQuery } from 'layouts/RealTimeDashboard/hooks/reportQueries'
import { useEndUsers } from 'layouts/RealTimeDashboard/hooks/useEndUsers'
import { usePaginatedTableData } from 'layouts/RealTimeDashboard/hooks/usePaginatedTableData'
import { OrgUser } from 'layouts/RealTimeDashboard/hooks/useTeamsWithMembers'
import { useRTDContext } from 'layouts/RealTimeDashboard/Overview/context'
import { toTideTableRow } from 'utils/component'
import { getSortedTableData, getTableColumns } from 'utils/tableSorting'
import { trackEvent } from 'utils/tracking'

import styles from '../../styles.module.css'

const QualifiedLeadsDrilldown: React.FC<ModalContentProps> = (
  props
): JSX.Element => {
  const context = useRTDContext()
  const { teams, isSalesView, filteredAgents, allUsers, bot } = context
  const cqlData = useLeadsQualifiedQuery()
  const leadsLoading = !cqlData?.data
  const conversationsData = useMemo(() => cqlData?.data ?? [], [cqlData])
  const conversationsError = cqlData.error
  const users = useMemo(() => Object.values(allUsers), [allUsers])
  const endUserIds = useMemo(() => {
    return conversationsData
      .map((c) => c['endUserId'])
      .filter((id) => !!id) as string[]
  }, [conversationsData])
  const uniqueEndUserIds = useMemo(() => makeUnique(endUserIds), [endUserIds])
  const { data: endUsers } = useEndUsers(uniqueEndUserIds)
  // @cmcgorrill - temporarily removing until we can refactor a better way to handle online presence for end users
  // const onlineEndUsers = useEndUserPresenceSubscription(endUsers ?? [])

  const COLUMNS = useMemo(
    () => [
      {
        trackLabel: 'Contact',
        label: 'Contact',
      },
      {
        trackLabel: 'Company',
        label: 'Company',
      },
      {
        trackLabel: 'Teammate',
        label: 'Teammate',
      },
      {
        trackLabel: 'Team',
        label: 'Team',
      },
      {
        trackLabel: 'Qualified by',
        label: 'Qualified by',
      },
      {
        trackLabel: 'Lead score',
        label: 'Lead score',
      },
      {
        trackLabel: 'Time',
        label: 'Time',
      },
      {
        trackLabel: 'View conversation',
        label: '',
      },
    ],
    []
  )

  const getTableData = () => {
    const teamValues = Object.values(teams)
    let agentsToShow = filteredAgents
    if (props.showTeamQualifiedLeads) {
      const agentsWithTeams = teamValues.reduce((acc: OrgUser[], team) => {
        return acc.concat(team.members)
      }, [])
      if (filteredAgents.length > 0) {
        agentsToShow = filteredAgents.filter((id) =>
          agentsWithTeams.some((a) => `${a.id}` === id)
        )
      } else {
        agentsToShow = agentsWithTeams.map((a) => `${a.id}`)
      }
    }

    const conversationsToShow =
      agentsToShow.length > 0
        ? conversationsData.filter(
            (convo) =>
              convo?.latestCQLData?.authorId &&
              agentsToShow.includes(`${convo.latestCQLData.authorId}`)
          )
        : conversationsData

    return conversationsToShow.map((convo) => {
      const { latestCQLData, endUserId, id: convoId } = convo

      const {
        updatedAt: timestamp,
        score: cqlScore,
        authorId: agentId,
        authorFirstAgent: agentIsParticipant,
        source: cqlSource,
      } = latestCQLData

      const qualifiedBy =
        cqlSource === 'BOT'
          ? QualifiedBy.BOT
          : agentIsParticipant
          ? QualifiedBy.AGENT
          : QualifiedBy.NONPARTICIPANT

      const convoEndUser = endUsers?.find((user) => {
        return `${user.id}` === `${endUserId}`
      })
      // @cmcgorrill - temporarily removing until we can refactor a better way to handle online presence for end users
      // const endUserIsOnline = !!onlineEndUsers?.has(+convoEndUser?.id)
      const companyInfo = convoEndUser?.endUserEnrichment?.company
      const endUserCompany = companyInfo?.data?.name

      const foundAgent =
        agentId === bot?.id
          ? bot
          : users?.find((u) => agentId && u.id === Number(agentId))
      const team = teamValues.find((t) =>
        t.members.some((member) => agentId && member.id === Number(agentId))
      )
      const agentIsOnline =
        Object.values(allUsers).find((a) => agentId && a.id === Number(agentId))
          ?.availability === AvailabilityIF.AVAILABLE

      const formattedTime = moment(timestamp).format('h:mm a')

      const data = [
        toTideTableRow(
          // @cmcgorrill - temporarily removing until we can refactor a better way to handle online presence for end users
          // renderUserCellWithAvailability(
          //   { ...convoEndUser, availability: endUserIsOnline },
          //   'Site visitor'
          // )
          renderUserCell(convoEndUser, 'Site visitor')
        ),
        toTideTableRow(endUserCompany ? endUserCompany : '--'),
        toTideTableRow(
          renderUserCellWithAvailability({
            ...foundAgent,
            availability: agentIsOnline,
          })
        ),
        toTideTableRow(team?.name ? team.name : '--'),
        toTideTableRow(renderQualifiedByCell(qualifiedBy)),
        toTideTableRow(renderCQLScoreCell(cqlScore), { value: cqlScore }),
        toTideTableRow(formattedTime, { value: timestamp }),
        toTideTableRow(
          renderGoToChatButton(convoId, isSalesView, 'qualified leads')
        ),
      ]

      return {
        data,
      }
    })
  }

  const [sortKey, setSortKey] = useState<number>(6)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const COL_LABELS = COLUMNS.map((c) => c.label)
  const MAX_WIDTH_TIME = 85
  const TABLE_COLUMNS = getTableColumns(
    COL_LABELS,
    {
      6: MAX_WIDTH_TIME,
    },
    {},
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection
  ).map((c, idx) => {
    c.sortable = idx === 6 || idx === 5
    return c
  })

  const tableData = getTableData()

  const sortedTableData = getSortedTableData(tableData, sortKey, sortDirection)

  const { totalPages, onChangePage, paginatedData, pageIndex, PAGE_SIZE } =
    usePaginatedTableData(sortedTableData, { sortKey, sortDirection }, 7)

  useEffect(() => {
    trackEvent('RTDBehavior', {
      event: 'Table Sorting',
      widget: 'RTDMissedChatDrilldown',
      column: COLUMNS[sortKey].trackLabel,
      view: isSalesView ? 'sales' : 'service',
    })
  }, [COLUMNS, isSalesView, sortKey])

  const [reportError, setReportError] = useState<boolean>(false)
  useEffect(() => {
    if (conversationsError) {
      setReportError(true)
    } else if (!conversationsError) {
      setReportError(false)
    }
  }, [conversationsError])

  return leadsLoading || tableData.length > 0 ? (
    <>
      <ChartContainer
        data={tableData}
        renderData={() => (
          <>
            <Table
              isWorking={leadsLoading}
              columns={TABLE_COLUMNS}
              data={paginatedData}
            />
            {tableData.length && paginatedData.length === 0 && (
              <EmptyTablePlaceholder text="No data found for selected filters" />
            )}
          </>
        )}
        loading={leadsLoading}
        error={reportError}
        noDataTitle={'No data for selected teams or teammates'}
      />

      <Paginator
        className="reports-paginator"
        totalPages={totalPages}
        changeSize={() => null}
        index={pageIndex}
        size={PAGE_SIZE}
        goForward={() => onChangePage(1)}
        goBackward={() => onChangePage(-1)}
        sizes={[PAGE_SIZE]}
        position="static"
      />
    </>
  ) : (
    <div data-ocweb-id="RTDQualifiedLeadsNoData" className={styles.emptyState}>
      <ChartNoDataState
        title="The team is doing well!"
        subtitle={`We have not seen any qualified leads${
          filteredAgents.length > 0
            ? ' for the teams and agents currently selected'
            : ''
        }.`}
        icon={<SweetBolt />}
      />
    </div>
  )
}

export default QualifiedLeadsDrilldown
